/*
 * Copyright © 2020-2022 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2025-10-22 20:18:43
 */
import './index.scss'
import React, { useEffect, useState, useCallback } from 'react'
import { Drawer, Progress } from 'antd'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import {
  setExportListDrawerVisible,
  fetchExportTasksV3
} from '../../../actions/photo'
import download_task_icon from '../../../images/download_task.png'
import sensors from '../../../utils/sensors'
import desktopLib from '../../../utils/desktopLib'
import CONFIG from '../../../constants/config'

interface IZip {
  url: string
  title: string
  status: number
  progress: number
}

interface ZipProps {
  zip: IZip
  isMutiple?: boolean
  title?: string
}

interface TaskProps {
  task: {
    taskID: string
    title: string
    zips: IZip[]
  }
}

const Header = () => (
  <div className="export-list-header">
    <h3>下载记录</h3>
    <div
      style={{ fontSize: 14, fontWeight: 400, color: '#818592', marginTop: 8 }}
    >
      历史下载记录最长保留
      <span style={{ fontWeight: 600, color: '#666' }}>7天</span>
      ，请及时下载到本地
    </div>
  </div>
)

const DownloadButton: React.FC<ZipProps> = ({ zip, title }) => {
  const [isDownloaded, setIsDownloaded] = useState(false)
  const zipTaskFailed = zip.status === 3
  const zipTaskProcessing = zip.status === 1
  const exportTasks = useAppSelector((state) => state.photo.exportTasks)

  const onDownload = useCallback(() => {
    handleDrawerClickTrack('clickDownload', {
      string1: title
    })
    setIsDownloaded(true)
    localStorage.setItem(zip.url, '1')
    window.location.href = zip.url
  }, [zip])

  useEffect(() => {
    const downloaded = localStorage.getItem(zip.url)
    setIsDownloaded(!!downloaded)
  }, [])

  // 添加埋点上报信息
  const handleDrawerClickTrack = (actionType: string, params?: any) => {
    const downloadList = exportTasks.map((item, index) => {
      return {
        index,
        name: item.title.split(' - ')[0].trim(),
        photosNum: item.zips.length,
        time: Math.floor(new Date(item.title.split(' - ')[1]).getTime() / 1000)
      }
    })
    sensors.track('web_get_action_photo_all_download_record', {
      actionType: actionType,
      infoJson: JSON.stringify(downloadList),
      isDesktop: desktopLib.isDesktop,
      track_sign:
        'aother.b4414.action.web_get_action_photo_all_download_record',
      requirement_id: 382,
      ...params
    })
  }

  if (zipTaskProcessing) {
    return (
      <button disabled className="download-immediately">
        立即下载
      </button>
    )
  }
  if (zipTaskFailed) {
    return (
      <button disabled className="download-failed">
        任务失败
      </button>
    )
  }
  if (isDownloaded) {
    return (
      <button className="download-again" onClick={onDownload}>
        再次下载
      </button>
    )
  }
  return (
    <button className="download-immediately" onClick={onDownload}>
      立即下载
    </button>
  )
}

const Zip: React.FC<ZipProps> = ({ zip, isMutiple, title }) => {
  const [isDownloaded, setIsDownloaded] = useState(false)
  const zipTaskFailed = zip.status === 3
  const zipTaskCompleted = zip.status === 2 // 任务完成状态
  const zipTaskProcessing = zip.status === 1 // 任务处理中状态

  useEffect(() => {
    const downloaded = localStorage.getItem(zip.url)
    setIsDownloaded(!!downloaded)
  }, [zip.url])

  return (
    <div className="zip">
      {/* 处理中、失败、或已完成但已下载过时显示进度状态 */}
      {(zipTaskProcessing ||
        zipTaskFailed ||
        (zipTaskCompleted && isDownloaded)) && (
        <div className="zip-status">
          <span>
            {isMutiple ? zip.title + '--' : ''}处理进度：
            <span style={{ color: zipTaskFailed ? '#DF4545' : '#1890FF' }}>
              {zip.progress}%
            </span>
          </span>
          <DownloadButton zip={zip} title={title} />
        </div>
      )}

      {/* 任务完成且未下载时显示文案和按钮（隐藏处理进度和进度条） */}
      {zipTaskCompleted && !isDownloaded && (
        <div className="zip-completed-status">
          <span className="completed-text">处理已完成，请立即下载</span>
          <DownloadButton zip={zip} title={title} />
        </div>
      )}

      {/* 处理中、失败、或已完成但已下载过时显示进度条 */}
      {(zipTaskProcessing ||
        zipTaskFailed ||
        (zipTaskCompleted && isDownloaded)) && (
        <Progress
          size="small"
          showInfo={false}
          strokeWidth={4}
          strokeColor={{
            from: '#71BEF8',
            to: '#0093FF'
          }}
          percent={zip.progress}
        />
      )}
    </div>
  )
}

const Task: React.FC<TaskProps> = ({ task }) => {
  const isMutiple = task.zips.length > 1
  return (
    <div className="task">
      <h4>
        <img src={download_task_icon} />
        {task.title}
      </h4>
      {task.zips.length === 0 && (
        <div className="backend-running">下载任务处理中...</div>
      )}
      {isMutiple && <div className="mutiple-tip">下载任务较大，已自动拆分</div>}
      {task.zips.map((zip, zipIndex) => (
        <Zip
          key={zipIndex}
          isMutiple={isMutiple}
          zip={zip}
          title={task.title}
        />
      ))}
    </div>
  )
}

const DownloadHistory = ({
  dataSource,
  showDesktopClientPromo = false
}: {
  dataSource?: string
  /** 仅「全部照片」按水印内容列表为 true；文件夹/相册/精准搜索等勿传 */
  showDesktopClientPromo?: boolean
}) => {
  const dispatch = useAppDispatch()
  const visible = useAppSelector((state) => state.photo.exportListDrawerVisible)
  const exportTaskTimer = useAppSelector((state) => state.photo.exportTaskTimer)
  const exportTasks = useAppSelector((state) => state.photo.exportTasks)
  const isGroupVIP = useAppSelector((state) => state.group.isVip)
  const isSpaceOpen = useAppSelector(
    (state: any) => state.mySpace?.personalSpace?.isSpaceOpen ?? false
  )

  useEffect(() => {
    if (visible && !exportTaskTimer) {
      dispatch(fetchExportTasksV3())
      if (exportTasks.length > 0) {
        handleDrawerTrack('show')
      }
    }
  }, [visible, exportTaskTimer])

  // 添加埋点上报信息
  const handleDrawerTrack = (actionType: string, params?: any) => {
    const downloadList = exportTasks.map((item, index) => {
      return {
        index,
        name: item.title.split(' - ')[0].trim(),
        photosNum: item.title.split('张')[0].slice(-1),
        time: Math.floor(new Date(item.title.split(' - ')[1]).getTime() / 1000)
      }
    })
    const trackParams = {
      actionType: actionType,
      infoJson: JSON.stringify(downloadList),
      isDesktop: desktopLib.isDesktop,
      track_sign:
        'aother.b4414.action.web_get_action_photo_all_download_record',
      requirement_id: 382,
      ...params
    }
    if (dataSource) {
      trackParams.fromPlace =
        dataSource === 'precise' ? 'preciseSearch' : 'watermarkContent'
    }
    sensors.track('web_get_action_photo_all_download_record', trackParams)
  }

  const onDesktopPromoClick = useCallback(() => {
    sensors.track('web_workgroup_nav_menu_cli', {
      path: 'downloadClient_record',
      isGroupVIP,
      bool1: isSpaceOpen
    })
    window.location.href = CONFIG.DESKTOP_DOWNLOAD_URL
  }, [isGroupVIP, isSpaceOpen])

  return (
    <Drawer
      className="export-list"
      getContainer={false}
      closable={true}
      keyboard={false}
      title={<Header />}
      size={415}
      open={visible}
      onClose={() => {
        dispatch(setExportListDrawerVisible(false))
        handleDrawerTrack('close')
      }}
    >
      {showDesktopClientPromo && !desktopLib.isDesktop && (
        <div className="export-list-desktop-hint">
          <span className="export-list-desktop-hint__text">
            浏览器下载压缩包需解压；电脑客户端可直接下载到本地文件夹，省去解压步骤。
          </span>
          <a
            href={CONFIG.DESKTOP_DOWNLOAD_URL}
            className="export-list-desktop-hint__link"
            onClick={(e) => {
              e.preventDefault()
              onDesktopPromoClick()
            }}
          >
            下载客户端
          </a>
        </div>
      )}
      <div className="task-list">
        {exportTasks.map((task, index) => (
          <Task key={task.taskID} task={task} />
        ))}
      </div>
    </Drawer>
  )
}

export default DownloadHistory
